import React, { useState, useEffect } from "react";
import { useFormatter } from "./../../util/formatter";
import { useUser } from "./../../util/user";
import OnPeakOffPeakInfo from "../general/OnPeakOffPeakInfo";
import { Col } from "react-bootstrap";
import "./dashboard.css";
import { useSaving, useSolarCalc } from "../../util/calculations";
import usePeakLabels from "../../util/peakLabels";
import moment from "moment-timezone";

function DashboardPeriodData(props) {
	const [formatNumber] = useFormatter();
	const [styleVisibility, setStyleVisibility] = useState("container-fluid");
	const [country, setCountry] = useState(null);
	const [currency, setCurrency] = useState(null);
	const [currentDuration, setCurrentDuration] = useState(null);
	const [data, setData] = useState(null);
	const [dataReady, setDataReady] = useState(false);
	const [gridObj, setGridObj] = useState(null);
	const [isFeedinTariff, setIsFeedinTariff] = useState(false);
	const [solarObj, setSolarObj] = useState(null);
	const [totalObj, setTotalObj] = useState(null);
	const solarCalc = useSolarCalc();

	const user = useUser();
	const roles = user.roles;
	const isGYUser = roles.includes("_GY:Base");

	const peakLabel = usePeakLabels(country);

	const durationMoment = currentDuration === 'THIS_MONTH' ? moment().startOf('month') : currentDuration === 'LAST_MONTH' ? moment().subtract(1, 'months').startOf('month') : currentDuration === 'THIS_YEAR' ? [moment().startOf('year'), moment().endOf('year')] : null;
	const savingData = useSaving({ table_names: props.selectedSite?.table_name, month: durationMoment, is_sum_by_site: currentDuration === 'THIS_YEAR'});
	const savingFmt = savingData.length > 0 ? savingData[0].saving.toLocaleString('en-US', { maximumFractionDigits: 2, minimumFractionDigits: 2 }) : 0;

	/**
	 * Revert data back to initial values
	 */
	const unsetData = () => {
		setSolarObj({
			solar: 0,
			solarON: 0,
			solarOFF: 0,
		});
		setGridObj({
			grid: 0,
			gridON: 0,
			gridOFF: 0,
		});
		setTotalObj({
			total: 0,
			totalON: 0,
			totalOFF: 0,
		});
	};

	/**
	 * Calculate data based on formula passed
	 * @param {Formula to be calculated} formula
	 * @param {Data object} data
	 */
	const calculateFormula = (formula, data) => {
		let answer = 0;
		if (data != null) {
			const irrad = data.irradiance;

			switch (formula) {
				case "PR":
					answer = (data.solar / ((irrad / 1000 / 6) * data.capacity)) * 100;
					break;

				case "CO2_TREES":
					const co2 = Math.round(data.solar * 0.505);
					const trees = Math.round(co2 / 22.5);
					answer = [co2, trees];
					break;

				case "SAVING":
					let prod = {
						ON: data.solarON,
						OFF: data.solarOFF,
						TOTAL: data.solar,
					};
					let price = {
						ON: data.price_on,
						OFF: data.price_off,
						FT: data.price_FT,
					};
					let discount = {
						ON: data.discount_on,
						OFF: data.discount_off,
						FT: data.discount_FT,
					};
					const currency = data.currency;

					const savObj = solarCalc.calculateSavings(
						prod,
						price,
						discount,
						currency
					);

					const savings = solarCalc.parseFormatNumber(savObj.savingsTotal, 2);

					answer = savings;
					break;

				case "ENERGY":
					answer = Math.round(
						data.solarOFF * data.price_off + data.solarON * data.price_on
					);
					break;

				default:
					answer = 0;
					break;
			}
		}
		return answer;
	};

	useEffect(() => {
		if (props.data != null) {
			setData(props.data);
			const solarTemp = {
				solar: props.data.solar,
				solarON: props.data.solarON,
				solarOFF: props.data.solarOFF,
			};
			const gridTemp = {
				grid: props.data.grid,
				gridON: props.data.gridON,
				gridOFF: props.data.gridOFF,
			};
			const totalTemp = {
				total: parseFloat(props.data.grid) + parseFloat(props.data.solar),
				totalON: parseFloat(props.data.gridON) + parseFloat(props.data.solarON),
				totalOFF:
					parseFloat(props.data.gridOFF) + parseFloat(props.data.solarOFF),
			};

			setSolarObj(solarTemp);
			setGridObj(gridTemp);
			setTotalObj(totalTemp);
			setDataReady(true);

			const currency = props.selectedSite.currency;
			const country = props.selectedSite.country;
			const tempIsFeedinTariff =
				props.selectedSite.is_feedintariff === 1 ? true : false;

			setCurrency(currency);
			setCountry(country);
			setIsFeedinTariff(tempIsFeedinTariff);
		}
		return () => {
			setDataReady(false);
			setData(null);
		};
	}, [props]);

	return (
		solarObj != null &&
		gridObj != null && (
			<>
				<div id="under-nav" className="container-fluid">
					<div className="row selectSite">
						<Col md="6">
							{isGYUser && (
								<div id="contract-desc">
									<span>
										<i
											className="glyphicon glyphicon-dashboard"
											style={{
												marginRight: "5px",
											}}
										></i>
										{props.selectedSite.contract_desc}
									</span>
								</div>
							)}
						</Col>
						<Col md="6">
							<div id="select-range">
								<ul>
									<li>
										<button
											id="moveup"
											className="btn btn-success"
											onClick={() => {
												setStyleVisibility("container-fluid");
												props.setShow("animateUp");
												unsetData();
												props.setSelectedDuration(null);
											}}
										>
											Close
										</button>
									</li>
									<li>
										<button
											className="movedown btn btn-success"
											onClick={() => {
												setStyleVisibility("container-fluid show");
												props.setShow("animateDown");

												if (currentDuration != "THIS_YEAR") {
													setCurrentDuration("THIS_YEAR");
													props.setSelectedDuration("THIS_YEAR");
												}
											}}
										>
											This year
										</button>
									</li>
									<li>
										<button
											className="movedown btn btn-success"
											onClick={() => {
												setStyleVisibility("container-fluid show");
												props.setShow("animateDown");

												if (currentDuration != "LAST_MONTH") {
													setCurrentDuration("LAST_MONTH");
													props.setSelectedDuration("LAST_MONTH");
												}
											}}
										>
											Last month
										</button>
									</li>
									<li>
										<button
											className="movedown btn btn-success"
											onClick={() => {
												setStyleVisibility("container-fluid show");
												props.setShow("animateDown");

												if (currentDuration != "THIS_MONTH") {
													setCurrentDuration("THIS_MONTH");
													props.setSelectedDuration("THIS_MONTH");
												}
											}}
										>
											this month
										</button>
									</li>
								</ul>
							</div>
						</Col>
					</div>
				</div>
				{dataReady && data != null && (
					<div className={styleVisibility} id="infos">
						<div id="content-infos" style={{ display: "block" }}>
							<div className="col-md-8 left">
								<h6>
									Solar Energy:
									<span id="solar"> {formatNumber(solarObj.solar, 0)}</span> kWh
									{
										<OnPeakOffPeakInfo
											peakLabel={peakLabel}
											data={{ ON: solarObj.solarON, OFF: solarObj.solarOFF }}
										/>
									}
								</h6>
								{!isFeedinTariff && (
									<>
										<h6>
											Grid Energy (Indicative):{" "}
											<span id="grid">{formatNumber(gridObj.grid, 0)}</span> kWh
											{country !== "VN" && (
												<OnPeakOffPeakInfo
													peakLabel={peakLabel}
													data={{ ON: gridObj.gridON, OFF: gridObj.gridOFF }}
												/>
											)}
										</h6>
										<h6>
											Total Consumption:{" "}
											<span id="total">{formatNumber(totalObj.total, 0)}</span>{" "}
											{country !== "VN" && (
												<OnPeakOffPeakInfo
													peakLabel={peakLabel}
													data={{
														ON: totalObj.totalON,
														OFF: totalObj.totalOFF,
													}}
												/>
											)}
										</h6>
									</>
								)}
								<h6>
									Performance Ratio:{" "}
									<span id="PR">{formatNumber(data.brut_PR, 2)}</span> %
								</h6>
							</div>

							<div className="col-md-4 right">
								{country === "TH" && (
									<>
										<h6>
											Value of the energy produced:{" "}
											<span id="energy_value">
												{formatNumber(calculateFormula("ENERGY", data), 0)}
											</span>{" "}
											{currency}
										</h6>
										<h6>
											Money savings:{" "}
											<span id="savings">
												{savingFmt}
											</span>{" "}
											{currency}
										</h6>
									</>
								)}
								<h6>
									CO<sub>2</sub> Avoided:{" "}
									<span id="co2">
										{formatNumber(calculateFormula("CO2_TREES", data)[0], 0)}
									</span>{" "}
									kg
								</h6>
								<h6>
									Trees Planted:{" "}
									<span id="trees">
										{formatNumber(calculateFormula("CO2_TREES", data)[1], 0)}
									</span>
								</h6>
							</div>
						</div>
					</div>
				)}
			</>
		)
	);
}

export default DashboardPeriodData;
